import { useTranslation } from 'react-i18next';
import { Spinner, Button, Image } from "@heroui/react";
import { useImageUpload } from '~/hooks/useImageUpload';
import { ImagesEntity } from '@bitecritic-api/src/images/images.zod';
import { ImageUploadType } from '@bitecritic-api/src/images/images.service';
import { FaTimes, FaPlay, FaCamera, FaFolder } from 'react-icons/fa';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB for videos
const MAX_FILES = 10;
const ALLOWED_TYPES = [
  // Images
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  // Videos
  'video/mp4',
  'video/quicktime',
  'video/x-msvideo',
  'video/webm'
];

interface ImageUploadProps {
  type: ImageUploadType;
  multiple?: boolean;
  onImageChange?: (image: ImagesEntity | null) => void;
  onImagesChange?: (images: ImagesEntity[]) => void;
  existingImages?: ImagesEntity[];
  maxFiles?: number;
}

export default function ImageUpload({
  type,
  multiple = false,
  onImageChange,
  onImagesChange,
  existingImages = [],
  maxFiles = MAX_FILES,
}: ImageUploadProps) {
  const { t } = useTranslation();
  const {
    uploadImage,
    deleteImage,
    uploadedImages,
    isUploading,
    isDeleting,
    uploadError,
    deleteError
  } = useImageUpload(existingImages);

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return t('errors.invalidFileType') || 'Invalid file type';
    }
    if (file.size > MAX_FILE_SIZE) {
      return t('errors.fileTooLarge', { size: '50MB' }) || 'File too large';
    }
    return null;
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList?.length) return;

    try {
      if (multiple) {
        if (uploadedImages.length + fileList.length > maxFiles) {
          throw new Error(t('errors.tooManyFiles', { max: maxFiles }) || `Maximum ${maxFiles} files allowed`);
        }

        const files = Array.from(fileList).filter((file): file is File => file !== null);
        const validFiles = files.filter(file => !validateFile(file));
        const uploadPromises = validFiles.map(file => uploadImage(file, type));
        const results = await Promise.all(uploadPromises);
        const newImages = [...uploadedImages, ...results];
        onImagesChange?.(newImages);
      } else if (fileList[0]) {
        const file = fileList[0];
        const error = validateFile(file);
        if (error) {
          throw new Error(error);
        }
        const result = await uploadImage(file, type);
        onImageChange?.(result);
      }
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleDelete = async (imageId: string) => {
    try {
      await deleteImage(imageId);
      if (multiple) {
        const updatedImages = uploadedImages.filter(img => img.id !== imageId);
        onImagesChange?.(updatedImages);
      } else {
        onImageChange?.(null);
      }
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const isUploadDisabled = isUploading || (multiple && uploadedImages.length >= maxFiles);

  const isVideo = (url: string) => /\.(mp4|mov|avi|webm)$/i.test(url);

  return (
    <div className="relative rounded-lg">
      <input
        type="file"
        onChange={handleFileChange}
        accept={ALLOWED_TYPES.join(',')}
        multiple={multiple}
        disabled={isUploadDisabled}
        className="hidden"
        id="image-upload"
      />

      <label
        htmlFor="image-upload"
        className={`bg-content1 cursor-pointer flex items-center justify-center p-4 border-2 rounded-lg transition-colors ${isUploadDisabled ? 'border-default-300 cursor-not-allowed' : 'hover:border-primary'
          }`}
      >
        {isUploading ? (
          <Spinner size="sm" color="primary" />
        ) : (
          <div className="flex items-center gap-2">
            <FaCamera size={36} className="text-secondary-500" />
            <FaFolder size={36} className="text-secondary-500" />
            <span>
              {isUploadDisabled
                ? t('common.maxFilesReached', { max: maxFiles })
                : t('common.uploadMedia')}
            </span>
          </div>
        )}
      </label>

      {uploadError && (
        <p className="text-danger text-small mt-2">
          {uploadError.message}
        </p>
      )}

      {deleteError && (
        <p className="text-danger text-small mt-2">
          {deleteError.message}
        </p>
      )}

      { uploadedImages.length > 0 && <div className={`flex flex-col gap-4 mt-4`}>
        {uploadedImages.map((media) => (
          <div key={media.id} className="relative group">
            {isVideo(media.url) ? (
              <div className="relative w-full bg-default-100 rounded flex items-center justify-center">
                <video
                  src={`${media.url}#t=0.01`}
                  className="w-full h-32 object-cover rounded"
                  controls={true}
                />
                <FaPlay className="absolute text-2xl text-white" />
              </div>
            ) : (
              <Image
                src={media.url}
                alt={t('common.uploadedMedia') || ''}
                width={'100%'}
                height={'auto'}
                className="w-full h-32 object-cover rounded"
              />
            )}
            <Button
              isIconOnly
              size="sm"
              color="danger"
              variant="solid"
              onPress={() => handleDelete(media.id)}
              isDisabled={isDeleting}
              className="absolute top-2 right-2 transition-opacity z-10"
              aria-label={t('common.deleteMedia') || 'Delete media'}
            >
              <FaTimes />
            </Button>
          </div>
        ))}
      </div>}
    </div>
  );
}